import { useState, useCallback, useRef } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

export function ChipInputRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()
  const initial = (element.options as any)?.chips
  const [chips, setChips] = useState<string[]>(
    Array.isArray(initial) ? initial : [],
  )
  const [inputValue, setInputValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  const handleAdd = useCallback(() => {
    const val = inputValue.trim()
    if (val && !chips.includes(val)) {
      const next = [...chips, val]
      setChips(next)
      setInputValue("")
      dispatchEvent(element.id, "change", { chips: next })
    }
  }, [inputValue, chips, element.id, dispatchEvent])

  const handleRemove = useCallback(
    (chip: string) => {
      const next = chips.filter((c) => c !== chip)
      setChips(next)
      dispatchEvent(element.id, "change", { chips: next })
    },
    [chips, element.id, dispatchEvent],
  )

  return (
    <div
      className={cn("flex flex-col gap-2", element.className)}
      data-testid={`chip-input-${element.id}`}
    >
      <div className="flex flex-row flex-wrap gap-1.5">
        {chips.map((chip) => (
          <span
            key={chip}
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-secondary text-secondary-foreground text-sm"
          >
            {chip}
            <button
              type="button"
              className="bg-transparent border-none cursor-pointer p-0 text-muted-foreground hover:text-foreground"
              onClick={() => handleRemove(chip)}
              aria-label={`Remove ${chip}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      <input
        ref={inputRef}
        type="text"
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        placeholder={element.placeholder ?? "Add tag..."}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault()
            handleAdd()
          }
        }}
        aria-label={element.ariaLabel ?? "Add new tag"}
      />
    </div>
  )
}
